import React from "react";
import styled from "styled-components";

import installationp1 from "../images/FeaturedProjects/JamaisVu/installationphoto1.jpg";
import DesignNarrative from "../images/FeaturedProjects/JamaisVu/DesignNarrative.png";
import TextShift from "../utils/TextShift";
import RevealOnScroll from "../utils/RevealOnScroll"; // assuming RevealOnScroll is imported from this location
import installationp2 from "../images/FeaturedProjects/JamaisVu/installationphoto2.jpg";
import poster1 from "../images/FeaturedProjects/JamaisVu/Poster1.png";
import poster2 from "../images/FeaturedProjects/JamaisVu/Poster2.png";
import installationskecth from "../images/FeaturedProjects/JamaisVu/InstallationSketch.png";

const PageWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 60%;
  margin: 0 auto;

  @media (max-width: 768px) {
    width: 95%;
    padding: 0.5rem;
  }
`;

const ShowcaseWrapper = styled.div`
  flex: 1;
  width: 80%;
  margin: 0 auto;
  padding-top: 2rem;

  @media (max-width: 768px) {
    width: 100%;
    padding-top: 1rem;
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const ImageItem = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  grid-column: span ${(props) => (props.isWide ? 2 : 1)};

  @media (max-width: 768px) {
    grid-column: span 1;
    margin-bottom: 0.5rem;
  }
`;

const Section = styled.section`
  margin-bottom: 2rem;
  scroll-margin-top: 150px;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    scroll-margin-top: 100px;
  }
`;

const SectionTitle = styled.h2`
  color: white;
  font-family: "Maitree", sans-serif;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
`;

const SectionText = styled.p`
  color: #b5b5b5;
  font-family: "Maitree", sans-serif;
  line-height: 1.6;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.5;
    margin-bottom: 0.75rem;
  }
`;

const MediaContainer = styled.div`
  margin: 1rem 0;

  iframe {
    width: 100%;
    height: 400px;
    border-radius: 8px;
  }

  @media (max-width: 768px) {
    margin: 0.5rem 0;
    
    iframe {
      height: 300px;
    }
  }
`;

const ResponsiveImage = styled.img`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "auto"};
  border-radius: 8px;
  margin: 1rem 0;
  padding: 10px;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    margin: 0.5rem 0;
    padding: 5px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem 0;
`;

const images = [
  { src: poster1, alt: "Project Image 1", isWide: false },
  { src: poster2, alt: "Project Image 2", isWide: false },
  { src: installationp1, alt: "Project Image 3", isWide: true },
  { src: installationp2, alt: "Project Image 4", isWide: true },
];

const JamaisVu = () => {
  // Smooth scrolling function

  return (
    <div>
      <PageWrapper>
    
        <ShowcaseWrapper>
        <RevealOnScroll>
        <TitleWrapper>
            <TextShift 
              text="Jamais Vu" 
              fontFamily="moret-variable"
              fontSize="2rem"
            />
          </TitleWrapper>
          </RevealOnScroll>
          <Section id="image-reel">
            <ImageGrid>
              {images.map((img, idx) => (
                <ImageItem
                  key={idx}
                  src={img.src}
                  alt={img.alt}
                  isWide={img.isWide}
                />
              ))}
            </ImageGrid>
          </Section>

          <Section id="overview">
            <SectionTitle>Abstract</SectionTitle>
            <SectionText>
              Jamais Vu is an immersive synaesthetic installation that
              transforms a gallery space into a dynamic multisensory
              environment. Our project takes its name "Jamais Vu" from the
              phenomenon of experiencing unfamiliarity when encountering
              something that should be familiar. The project seeks to disrupt
              habitual familiarity with our increasingly highly digitized world,
              in which more and more aspects of our lives are being tracked,
              measured, and converted into data, reducing people from complex
              individuals into quantifiable units of information. We aimed to
              evoke this sensation by creating a (syn)aesthetic installation
              featuring a soundscape composed of crowdsourced audio samples from
              across the city of Montreal. This defamiliarization may prompt an
              understanding of how the present actively intrudes upon, reshapes,
              and haunts our possible imaginations of the future.
            </SectionText>
          </Section>

          <Section id="installation-design">
            <SectionTitle>Installation Design</SectionTitle>
            <MediaContainer>
              <ResponsiveImage
                src={DesignNarrative}
                alt="Installation View 1"
                width="500px"
                height="auto"
                mobileWidth="200px"
                mobileHeight="auto"
              />
              <ResponsiveImage
                src={installationskecth}
                alt="Installation View 1"
                width="500px"
                height="auto"
                mobileWidth="200px"
                mobileHeight="auto"
              />
            </MediaContainer>
            <SectionText>
              We iterated on the installation layout multiple times to create a
              welcoming and explorable experience. Using black translucent
              fabric and projected visuals, we simulated an urban space. The
              final design featured avenues and streets guiding the audience
              forward, ending with a bench flanked by lights and an interactive
              device. The layout emphasized flow and user engagement,
              encouraging intuitive navigation and interaction with the
              elements.
            </SectionText>
          </Section>

          <Section id="visuals">
            <SectionTitle>Visuals</SectionTitle>
            <MediaContainer>
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/FdNCfNnk0-o?si=DmkDeOhO3jpfFmNR"
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerpolicy="strict-origin-when-cross-origin"
                allowfullscreen
              ></iframe>
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/Wur_B0jm2yA?si=HVAi0xmHy6jRh9T_&amp;controls=0"
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerpolicy="strict-origin-when-cross-origin"
                allowfullscreen
              ></iframe>
            </MediaContainer>
            <SectionText>
              We refined our process using the Blender add-on Blosm to convert
              Google Maps tiles into 3D meshes, which we processed in
              CloudCompare to create point clouds. These point clouds were
              integrated into TouchDesigner for displacement effects and audio
              reactivity, using input from the device and the soundscape.
              Expanding to a larger tile of downtown Montreal, we achieved
              visual cohesion by aligning warped visuals with audio clusters.
              While placeholders were used in early stages, the final visuals
              were reusable and thematically consistent with the project.
            </SectionText>
          </Section>
        </ShowcaseWrapper>
      </PageWrapper>
    </div>
  );
};

export default JamaisVu;
